import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { CustomerCouponCard } from "components";
import ScrollRail from "components/ScrollRail/ScrollRail";

import { RouteNames } from "../../../../../constants";

const CUSTOMER_LOGIN_ROUTE = "/customer-login";

const categories = [
  "Haircut",
  "Coffee",
  "Car Repair",
  "Pizza",
  "Fitness",
];

interface DealsCategoryBannerSectionProps {
  // Real coupons for the "Coupons for Everyone" row — falls back to an
  // empty row (no fake placeholder cards) while data is still loading.
  coupons?: any[];
}

export default function DealsCategoryBannerSection({ coupons = [] }: DealsCategoryBannerSectionProps) {
  const navigate = useNavigate();

  const [itemHeight, setItemHeight] = useState(60);

  useEffect(() => {
    const updateHeight = () => {
      const w = window.innerWidth;
      if (w >= 1850) setItemHeight(120);
      else if (w >= 1199) setItemHeight(75);
      else setItemHeight(60);
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % categories.length);
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  const goToDetails = (coupon: any) => {
    navigate(`${RouteNames.COUPONS_DETAILS}/${coupon.id}`);
  };

  const goToBusiness = (coupon: any) => {
    if (coupon?.business?.id) {
      const route = coupon.is_affiliate_or_flyer ? RouteNames.AFFILIATE_BUSINESS_DETAILS : RouteNames.BUSINESSES_DETAILS;
      navigate(`${route}/${coupon.business.id}`);
    }
  };

  const requireLogin = () => navigate(CUSTOMER_LOGIN_ROUTE);

  return (
    <section className="w-full flex justify-center">
      <div className="w-full">

        <div className="
          relative
          w-full
          rounded-[32px]
          overflow-hidden
          bg-gradient-to-t from-[#c9c9c9] via-[#e8e8e8] to-white
          py-[50px] lg:py-[80px]
        ">

          {/* CONTENT */}
          <div className="relative z-10 flex flex-col items-center justify-center text-center px-4">

            {/* badge */}
            <div className="
              flex items-center gap-2
              bg-neutral-900
              rounded-full
              px-5
              py-2
              shadow-md
              mb-6
            ">
              <span className="text-white text-sm">★</span>
              <span className="text-white text-sm font-medium">
                Over 50 Categories
              </span>
            </div>

            {/* heading */}
            <h2 className="
              text-neutral-900
              text-[28px] lg:text-[40px]
              font-medium
              leading-tight
            ">
              Discover New Deals Every Day
            </h2>

            {/* animated category */}
            <div className="
              relative
              h-[72px]
              mt-2
              flex
              items-center
              justify-center
              overflow-visible
            ">

              <div className="overflow-hidden h-[60px] min-[1199px]:h-[75px] min-[1850px]:h-[120px]">
                <div
                  className="transition-transform duration-500"
                  style={{ transform: `translateY(-${currentIndex * itemHeight}px)` }}
                >
                  {categories.map((text, index) => (
                    <div
                      key={index}
                      className="font-semibold text-neutral-900 text-center"
                      style={{
                        fontSize: 'clamp(36px, calc(36px + (64 - 36) * ((100vw - 1199px) / (1850 - 1199))), 64px)',
                        lineHeight: itemHeight + 'px',
                      }}
                    >
                      {text}
                    </div>
                  ))}
                </div>
              </div>

            </div>

            {/* subtext */}
            <p className="text-gray-600 mt-3 text-[14px] lg:text-[16px]">
              and thousands more deals across every category imaginable.
            </p>

          </div>

          {/* REAL COUPON CARDS */}
          {coupons.length > 0 && (
            <div className="relative z-10 mt-12 lg:mt-16 px-2 lg:px-10">
              <ScrollRail
                className="flex gap-5 px-5 overflow-x-auto pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                ariaLabel="coupons"
              >
                {coupons.slice(0, 6).map((coupon) => (
                  <div key={coupon.id} className="flex-shrink-0 w-[280px] lg:w-[320px]">
                    <CustomerCouponCard
                      data={coupon}
                      onPress={() => goToDetails(coupon)}
                      isFavourite={false}
                      goToBusinessDetails={() => goToBusiness(coupon)}
                      toggleFavourite={requireLogin}
                    />
                  </div>
                ))}
              </ScrollRail>
            </div>
          )}

        </div>

      </div>
    </section>
  );
}

const discoverTiles = [
  {
    image: "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=900&q=80",
    label: "Hotels",
    badge: "Now Up To 25% Off",
  },
  {
    image: "https://images.unsplash.com/photo-1552566626-52f8b828add9?auto=format&fit=crop&w=900&q=80",
    label: "Restaurant",
    badge: "Now Up To 25% Off",
  },
  {
    image: "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=900&q=80",
    label: "Shopping",
    badge: "Now Up To 25% Off",
  },
  {
    image: "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?auto=format&fit=crop&w=900&q=80",
    label: "Events",
    badge: "Now Up To 25% Off",
  },
  {
    image: "https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?auto=format&fit=crop&w=900&q=80",
    label: "Service",
    badge: "Now Up To 25% Off",
  },
  {
    image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=900&q=80",
    label: "Vacations",
    badge: "Now Up To 25% Off",
  },
];

// "Discover Deals That Matter" — decorative category grid from the new
// landing page design. Lives in this file (rather than its own component
// folder) so it's picked up by the dev server's TS watcher immediately.
export const DealsShowcaseSection = () => {
  return (
    <section className="w-full flex justify-center">
      <div className="w-full max-w-[1820px]">

        {/* HEADING */}
        <h2 className="text-[28px] lg:text-[40px] font-medium text-neutral-900 mb-6 lg:mb-10">
          Discover Deals That Matter
        </h2>

        {/* GRID — first column (Hotels / Events) wider than the other two */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.6fr_1fr_1fr] gap-4 lg:gap-6">
          {discoverTiles.map((tile, i) => (
            <div
              key={i}
              className="relative rounded-[20px] overflow-hidden h-[240px] lg:h-[300px] group"
            >
              <img
                src={tile.image}
                alt={tile.label}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/5 to-transparent" />

              <div className="absolute bottom-5 left-5 right-5">
                <p className="text-white text-[20px] lg:text-[24px] font-semibold leading-tight mb-3">
                  {tile.label}
                </p>
                <span className="inline-flex items-center bg-white text-neutral-900 text-[13px] font-medium px-4 py-2 rounded-full">
                  {tile.badge}
                </span>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};
